import { useId } from 'react';

// Кольори зерна за прогресом обсмажки (частка циклу 0…1) — ілюстративні
// «пігменти» з дизайн-хендофу, не кольори інтерфейсу, тому поза палітрою токенів:
// зелене → жовтіння → карамелізація → … → майже чорне.
const BEAN_STOPS: ReadonlyArray<[number, Rgb]> = [
  [0, [126, 154, 90]],
  [0.22, [176, 172, 104]],
  [0.42, [178, 126, 66]],
  [0.62, [122, 76, 40]],
  [0.8, [66, 40, 24]],
  [1, [30, 19, 12]],
];

// First crack настає на 214°, тобто на 5.2 с із 7.8-секундного циклу.
const CRACK_PROGRESS = 0.67;

const CHAFF = [0, 45, 95, 140, 190, 235, 280, 325].map((angle, index) => ({
  angle,
  delay: (index % 3) * 60,
  size: 8 + (index % 3) * 4,
}));

type Rgb = [number, number, number];

function beanColorAt(progress: number, dim = 1): string {
  // Відрізок шкали, у який потрапляє progress; за межами 0…1 — крайній відрізок.
  const segment = BEAN_STOPS.findIndex(([at], index) => index > 0 && progress <= at) - 1;
  const fromIndex = segment < 0 ? BEAN_STOPS.length - 2 : segment;
  const [fromAt, fromColor] = BEAN_STOPS[fromIndex] as [number, Rgb];
  const [toAt, toColor] = BEAN_STOPS[fromIndex + 1] as [number, Rgb];
  const t = Math.max(0, Math.min(1, (progress - fromAt) / (toAt - fromAt)));
  const channels = fromColor.map((value, k) =>
    Math.round((value + ((toColor[k] as number) - value) * t) * dim),
  );
  return `rgb(${channels.join(',')})`;
}

export function RoastingBean({ progress, cracked }: { progress: number; cracked: boolean }) {
  const highlightId = useId();
  const scale = 0.84 + 0.2 * Math.min(1, progress / CRACK_PROGRESS);

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none relative aspect-[3/4] w-[clamp(120px,17vw,240px)] shrink-0 self-start md:mr-[clamp(0px,calc(20vw-160px),268px)] md:self-auto"
    >
      {cracked && (
        <>
          <div className="absolute inset-[-10%] animate-crackRing rounded-full border-[1.5px] border-accent" />
          {CHAFF.map((piece) => (
            <div
              key={piece.angle}
              className="absolute left-1/2 top-1/2 h-0 w-0"
              style={{ transform: `rotate(${piece.angle}deg)` }}
            >
              <span
                className="absolute left-0 top-[-3px] h-1 animate-chaff rounded-full bg-chaff"
                style={{ width: piece.size, animationDelay: `${piece.delay}ms` }}
              />
            </div>
          ))}
        </>
      )}
      <div
        className={cracked ? 'h-full w-full animate-beanShake' : 'h-full w-full'}
        style={{
          transform: `rotate(-18deg) scale(${scale.toFixed(3)})`,
          transition: 'transform 200ms linear',
        }}
      >
        <svg
          aria-hidden="true"
          viewBox="0 0 120 160"
          width="100%"
          height="100%"
          className="overflow-visible drop-shadow-[0_24px_30px_theme(colors.bean-shadow/55%)]"
        >
          <defs>
            <radialGradient id={highlightId} cx="0.34" cy="0.3" r="0.75">
              <stop
                offset="0"
                className="[stop-color:theme(colors.bean-light)] [stop-opacity:0.28]"
              />
              <stop
                offset="0.45"
                className="[stop-color:theme(colors.bean-light)] [stop-opacity:0.05]"
              />
              <stop
                offset="1"
                className="[stop-color:theme(colors.bean-shadow)] [stop-opacity:0.38]"
              />
            </radialGradient>
          </defs>
          <ellipse cx="60" cy="80" rx="52" ry="72" fill={beanColorAt(progress)} />
          <ellipse cx="60" cy="80" rx="52" ry="72" fill={`url(#${highlightId})`} />
          <path
            d="M62 12 C 44 40, 78 60, 58 82 C 40 102, 74 124, 60 148"
            fill="none"
            stroke={beanColorAt(Math.min(1, progress + 0.28), 0.55)}
            strokeWidth="5"
            strokeLinecap="round"
          />
          <path
            d="M64 14 C 47 41, 81 61, 61 83 C 43 103, 77 124, 63 146"
            fill="none"
            className="stroke-bean-light"
            strokeOpacity="0.12"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <g
            className="stroke-bean-crack transition-opacity duration-[180ms]"
            style={{ opacity: cracked ? 1 : 0 }}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M58 82 L 36 70 L 28 76 L 14 66" strokeWidth="2.4" />
            <path d="M60 86 L 82 96 L 90 90 L 106 100" strokeWidth="2.4" />
            <path d="M36 70 L 32 58" strokeWidth="1.6" />
            <path d="M82 96 L 88 108" strokeWidth="1.6" />
          </g>
        </svg>
      </div>
    </div>
  );
}
